import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/** @type {boolean | undefined} */
let predictiveSearchSupported;

/**
 * Whether Shopify's Predictive Search API supports the current buyer locale.
 *
 * 予測検索は対応言語のセッションでしか動かず、日本語は対応言語に含まれない。
 * 非対応のときは Shopify が `<script id="shopify-features">` の `predictiveSearch` を false にする。
 * https://shopify.dev/docs/api/ajax/reference/predictive-search
 *
 * @returns {boolean} true when /search/suggest can be used
 */
function isPredictiveSearchSupported() {
  if (predictiveSearchSupported === undefined) {
    try {
      const features = document.getElementById('shopify-features');
      const parsed = features?.textContent ? JSON.parse(features.textContent) : null;

      // 情報が取れないときは従来どおり叩いてフォールバックに任せる
      predictiveSearchSupported = parsed?.predictiveSearch !== false;
    } catch {
      predictiveSearchSupported = true;
    }
  }

  return predictiveSearchSupported;
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Observer for the sentinel rendered at the end of the fallback search results.
   * @type {IntersectionObserver | null}
   */
  #infiniteScrollObserver = null;

  /**
   * Whether a next page request is in flight.
   * @type {boolean}
   */
  #loadingNextPage = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    onDocumentLoaded(() => {
      this.resetSearch(false); // Pass false to avoid focusing the input
    });
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#infiniteScrollObserver?.disconnect();
    this.#infiniteScrollObserver = null;
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    try {
      // 予測検索が非対応の言語（日本語など）では /search/suggest が必ず 417 を返す。
      // 打っても無駄なので最初から /search のフォールバックへ行く（1入力あたり約175ms の短縮）
      let resultsMarkup = isPredictiveSearchSupported()
        ? await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url)
        : null;

      // Workaround: Shopify returns 417 "Unsupported buyer locale" on some stores
      // (Markets/locale configuration). Fall back to /search endpoint which supports
      // search.performed in the section rendering.
      if (!resultsMarkup || resultsMarkup.includes('Expectation failed')) {
        resultsMarkup = await sectionRenderer.getSectionHTML(
          this.dataset.sectionId,
          false,
          this.#buildFallbackSearchUrl(searchTerm)
        );
      }

      if (!resultsMarkup || abortController.signal.aborted) return;

      morph(predictiveSearchResults, resultsMarkup);
      this.#resetScrollPositions();
      // スクロール位置が戻る前に観測すると、前の検索の位置のまま番兵が可視と判定されて
      // 2ページ目を無駄に読み込む。#resetScrollPositions の rAF の後に観測を始める
      requestAnimationFrame(() => this.#observeSearchResultsSentinel(searchTerm));
    } catch (error) {
      if (abortController.signal.aborted) return;
      throw error;
    }
  }

  /**
   * Builds the /search URL used when the predictive search endpoint is unavailable.
   * @param {string} searchTerm - The term to search for
   * @returns {URL} The URL of the fallback search request
   */
  #buildFallbackSearchUrl(searchTerm) {
    const fallbackUrl = new URL(Theme.routes.search_url, location.origin);
    fallbackUrl.searchParams.set('q', searchTerm);
    // 商品グリッドしか描画しないので、ページ送りの件数が記事やページでずれないよう商品に絞る
    fallbackUrl.searchParams.set('type', 'product');
    return fallbackUrl;
  }

  /**
   * Observes the sentinel at the end of the fallback search results so the popup keeps loading
   * products while the user scrolls. The predictive search endpoint returns a fixed set of
   * resources with no pagination, so only the /search fallback markup carries the sentinel.
   * @param {string} searchTerm - The term the currently rendered results belong to
   */
  #observeSearchResultsSentinel(searchTerm) {
    this.#infiniteScrollObserver?.disconnect();
    this.#infiniteScrollObserver = null;

    const sentinel = this.refs.predictiveSearchResults.querySelector('[data-search-view-more]');
    if (!(sentinel instanceof HTMLElement)) return;

    // root は viewport（null）で見る。結果一覧の祖先には overflow:auto が複数あり、そのうち
    // 実際にクリップしているのは1つだけなので、祖先を root に選ぶと「常に交差している」判定に
    // なって延々と次ページを読み込んでしまう。viewport 基準なら祖先のクリップも考慮される。
    this.#infiniteScrollObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#renderNextSearchPage(searchTerm);
      },
      { rootMargin: '200px' }
    );

    this.#infiniteScrollObserver.observe(sentinel);
  }

  /**
   * Appends the next page of search results to the popup grid.
   * @param {string} searchTerm - The term the currently rendered results belong to
   */
  async #renderNextSearchPage(searchTerm) {
    if (this.#loadingNextPage || !this.dataset.sectionId) return;

    const { predictiveSearchResults } = this.refs;
    const sentinel = predictiveSearchResults.querySelector('[data-search-view-more]');
    const grid = predictiveSearchResults.querySelector('[data-search-results-grid]');

    if (!(sentinel instanceof HTMLElement) || !(grid instanceof HTMLElement)) return;

    const nextPage = Number(sentinel.dataset.nextPage);
    const lastPage = Number(grid.dataset.lastPage);

    if (!nextPage || (lastPage && nextPage > lastPage)) return;

    this.#loadingNextPage = true;

    try {
      const url = this.#buildFallbackSearchUrl(searchTerm);
      url.searchParams.set('page', String(nextPage));

      const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      if (!markup) return;

      // 入力が変わって別の検索結果に差し替わっていたら、古いページを混ぜない
      if (this.refs.searchInput.value.trim() !== searchTerm) return;
      if (!grid.isConnected) return;

      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const cards = parsed.querySelectorAll('[data-search-results-grid] > li');

      if (cards.length) grid.append(...cards);

      const nextSentinel = parsed.querySelector('[data-search-view-more]');

      if (nextSentinel instanceof HTMLElement && nextSentinel.dataset.nextPage) {
        sentinel.dataset.nextPage = nextSentinel.dataset.nextPage;
      } else {
        this.#infiniteScrollObserver?.disconnect();
        this.#infiniteScrollObserver = null;
        sentinel.remove();
        return;
      }
    } finally {
      this.#loadingNextPage = false;
    }

    // 追加後も番兵が画面内に残っている場合があるので、観測し直して続きを読み込ませる
    this.#observeSearchResultsSentinel(searchTerm);
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#infiniteScrollObserver?.disconnect();
    this.#infiniteScrollObserver = null;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
